import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { throwIfNotOk } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs } from "@/lib/format";
import {
  Users,
  UserCheck,
  Calendar,
  TrendUp,
  ArrowRight,
} from "@phosphor-icons/react";
import { getSessionStatusBreakdownConfig } from "@/lib/status-configs";
import { BillingSummaryCard } from "@/components/supervisor/billing/BillingSummaryCard";

export const Route = createFileRoute("/dashboard/supervisor/")({
  component: SupervisorIndex,
});

interface MonthStat { month: string; label: string; sessions: number }
interface StatusBreakdown { status: string; count: number }
interface TopEmployee { name: string; email: string | null; sessionCount: number }
interface RecentSession {
  id?: string; scheduledAt: string; status: string; durationMinutes: number;
  clientName: string;
}

interface CompanyOverview {
  name: string;
  planType: string;
  maxEmployees: number;
  employeeCount: number;
  activeEmployeeCount: number;
  sessionsThisMonth: number;
  analytics: {
    monthlyStats: MonthStat[];
    statusBreakdown: StatusBreakdown[];
    topEmployees: TopEmployee[];
    recentSessions: RecentSession[];
  };
}

function SupervisorIndex() {
  const { t } = useTranslation();
  const STATUS_CONFIG = getSessionStatusBreakdownConfig(t);

  const { data, isLoading } = useQuery({
    queryKey: ["supervisor", "company"],
    queryFn: async () => {
      const res = await fetch("/api/supervisor/company");
      await throwIfNotOk(res, t("common.errorLoading"));
      return res.json() as Promise<CompanyOverview>;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-40 animate-pulse rounded-2xl border bg-card" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border bg-card" />
          ))}
        </div>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="h-64 animate-pulse rounded-xl border bg-card" />
          <div className="h-64 animate-pulse rounded-xl border bg-card" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const maxSessions = Math.max(...(data.analytics.monthlyStats.map((m) => m.sessions)), 1);

  return (
    <div className="space-y-6">
      {/* Hero header */}
      <div className="overflow-hidden rounded-2xl border border-border/60 shadow-sm" style={{ background: "linear-gradient(135deg, oklch(28% 0.04 84), oklch(22% 0.03 80))" }}>
        <div className="px-6 py-8 sm:px-8">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest" style={{ color: "oklch(65% 0.04 84)" }}>
              {data.planType}
            </p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl" style={{ fontFamily: "var(--font-heading)" }}>
              {data.name}
            </h1>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label={t("supervisor.dashboard.totalEmployees")} value={data.employeeCount} subtitle={`${t("supervisor.dashboard.of")} ${data.maxEmployees} max`} />
        <StatCard icon={UserCheck} label={t("supervisor.dashboard.active")} value={data.activeEmployeeCount} subtitle={t("supervisor.dashboard.acceptedInvite")} />
        <StatCard icon={Calendar} label={t("supervisor.dashboard.sessionsThisMonth")} value={data.sessionsThisMonth} accent />
        <StatCard icon={TrendUp} label={t("supervisor.dashboard.utilization")} value={`${data.maxEmployees > 0 ? Math.round((data.activeEmployeeCount / data.maxEmployees) * 100) : 0}%`} subtitle={t("supervisor.dashboard.capacity")} />
      </div>

      <BillingSummaryCard />

      {/* Two column: chart + status breakdown */}
      <div className="grid gap-5 lg:grid-cols-3">
        {/* Monthly chart */}
        <div className="lg:col-span-2 overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
          <div className="border-b border-border/40 px-5 py-3.5">
            <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("supervisor.dashboard.sessionsByMonth")}
            </h2>
          </div>
          <div className="p-5">
            <div className="flex items-end gap-2" style={{ height: 160 }}>
              {data.analytics.monthlyStats.map((m, i) => {
                const height = maxSessions > 0 ? (m.sessions / maxSessions) * 100 : 0;
                const isLast = i === data.analytics.monthlyStats.length - 1;
                return (
                  <div key={m.month} className="flex flex-1 flex-col items-center gap-1.5">
                    <span className="text-xs font-bold text-foreground">{m.sessions || ""}</span>
                    <div className="relative w-full flex justify-center" style={{ height: 120 }}>
                      <div
                        className="w-full max-w-10 rounded-t-md transition-all duration-500"
                        style={{
                          height: `${Math.max(height, 4)}%`,
                          background: isLast
                            ? "linear-gradient(180deg, oklch(70% 0.12 84), oklch(60% 0.15 84))"
                            : "oklch(90% 0.03 84)",
                          alignSelf: "flex-end",
                        }}
                      />
                    </div>
                    <span className={cn("text-[11px]", isLast ? "font-bold text-foreground" : "text-muted-foreground")}>
                      {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Status breakdown */}
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
          <div className="border-b border-border/40 px-5 py-3.5">
            <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("supervisor.dashboard.sessionStatusThisMonth")}
            </h2>
          </div>
          <div className="p-5 space-y-3">
            {data.analytics.statusBreakdown.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              data.analytics.statusBreakdown.map((sb) => {
                const cfg = STATUS_CONFIG[sb.status] ?? STATUS_CONFIG.scheduled;
                const StatusIcon = cfg.icon;
                const total = data.analytics.statusBreakdown.reduce((a, b) => a + b.count, 0);
                const pct = total > 0 ? Math.round((sb.count / total) * 100) : 0;
                return (
                  <div key={sb.status}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-2">
                        <StatusIcon className="size-3.5" style={{ color: cfg.color }} />
                        <span className="text-foreground">{cfg.label}</span>
                      </span>
                      <span className="font-semibold text-foreground">{sb.count}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "oklch(94% 0.01 84)" }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, background: cfg.color }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Bottom row: top employees + recent sessions */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Top employees */}
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border/40 px-5 py-3.5">
            <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("supervisor.dashboard.topEmployees")}
            </h2>
            <Link to="/dashboard/supervisor/employees" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              {t("common.all")} <ArrowRight className="size-3" />
            </Link>
          </div>
          <div className="divide-y divide-border/30">
            {data.analytics.topEmployees.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              data.analytics.topEmployees.map((emp, i) => (
                <div key={emp.email ?? i} className="flex items-center gap-3 px-5 py-3">
                  <div
                    className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                    style={{
                      background: i === 0 ? "linear-gradient(135deg, oklch(85% 0.08 84), oklch(78% 0.12 84))" : "oklch(94% 0.01 84)",
                      color: i === 0 ? "oklch(40% 0.12 84)" : "oklch(55% 0.02 84)",
                      fontFamily: "var(--font-heading)",
                    }}
                  >
                    {(emp.name || emp.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{emp.name || emp.email}</p>
                  </div>
                  <span className="shrink-0 text-sm font-bold text-foreground">{emp.sessionCount}</span>
                  <span className="text-xs text-muted-foreground">{t("supervisor.dashboard.sessionsLabel")}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent sessions */}
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border/40 px-5 py-3.5">
            <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("supervisor.dashboard.recentSessions")}
            </h2>
            <Link to="/dashboard/supervisor/sessions" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              {t("common.all")} <ArrowRight className="size-3" />
            </Link>
          </div>
          <div className="divide-y divide-border/30">
            {data.analytics.recentSessions.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t("supervisor.dashboard.noSessions")}</p>
            ) : (
              data.analytics.recentSessions.map((s, index) => {
                const cfg = STATUS_CONFIG[s.status] ?? STATUS_CONFIG.scheduled;
                const StatusIcon = cfg.icon;
                return (
                  <div key={s.id ?? index} className="flex items-center gap-3 px-5 py-3">
                    <div
                      className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                      style={{ background: "oklch(94% 0.02 84)", color: "oklch(50% 0.08 84)", fontFamily: "var(--font-heading)" }}
                    >
                      {s.clientName.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{s.clientName}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ background: cfg.bg, color: cfg.color }}
                      >
                        <StatusIcon className="size-2.5" />
                        {cfg.label}
                      </span>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatDateWithOptionsBs(s.scheduledAt, { day: "numeric", month: "short", timeZone: "Europe/Sarajevo" })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Stat Card ─── */

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  accent,
}: {
  icon: typeof Users;
  label: string;
  value: number | string;
  subtitle?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border/50 bg-card p-5 shadow-sm",
        accent && "ring-1 ring-primary/20"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div
          className="flex size-8 items-center justify-center rounded-lg"
          style={{ background: accent ? "oklch(90% 0.06 84)" : "oklch(96% 0.01 84)" }}
        >
          <Icon className="size-4" style={{ color: accent ? "oklch(50% 0.15 84)" : "oklch(55% 0.04 84)" }} />
        </div>
      </div>
      <p className="mt-2 text-2xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
